import posthog from 'posthog-js';

const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
const posthogHost = import.meta.env.VITE_POSTHOG_HOST;
const trackingAllowed = typeof window !== 'undefined'
  && window.location.hostname === 'app.co2impact.co.uk';

export const isPosthogConfigured = Boolean(trackingAllowed && posthogKey && posthogHost);

if (trackingAllowed && (!posthogKey || !posthogHost)) {
  if (import.meta.env.DEV) {
    const missingVariable = posthogKey ? 'VITE_POSTHOG_HOST' : 'VITE_POSTHOG_KEY';
    throw new Error(
      `${missingVariable} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once ${missingVariable} is configured`
    );
  }
} else if (isPosthogConfigured) {
  posthog.init(posthogKey, {
    api_host: posthogHost,
    defaults: '2026-05-30',
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    logs: {
      serviceName: 'co2front-web',
      environment: import.meta.env.MODE,
    },
  });
}

const posthogIntegrationLogger = {
  info: (message: string, attributes: Record<string, string | number | boolean>) => {
    if (!isPosthogConfigured) return;
    posthog.logger.info(message, attributes);
  },
};

if (isPosthogConfigured) {
  posthogIntegrationLogger.info('browser PostHog log capture configured', { runtime: 'browser' });
}

type IdentifiedUser = {
  id?: number | string;
  email?: string;
  first_name?: string;
  last_name?: string;
};

export const capturePosthog = (event: string, properties?: Record<string, unknown>) => {
  if (!isPosthogConfigured) return;
  posthog.capture(event, properties);
};

export const identifyUser = (user: IdentifiedUser) => {
  if (!isPosthogConfigured || user.id === undefined || user.id === null) return;

  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
  posthog.identify(String(user.id), {
    ...(user.email ? { email: user.email } : {}),
    ...(name ? { name } : {}),
  });
  posthogIntegrationLogger.info('authenticated browser session established', {
    authentication_source: 'protected_profile',
  });
};

export const resetPosthog = () => {
  if (!isPosthogConfigured) return;
  posthog.reset();
  posthogIntegrationLogger.info('browser analytics session reset', {
    reset_reason: 'application_logout',
  });
};

export default posthog;
